/**
 * Tooltip Component - Viewport-aware tooltip with click-outside dismissal
 *
 * Displays a tooltip that automatically positions itself to stay within viewport
 * on both desktop and mobile. Dismisses when clicking outside the tooltip.
 */

import React, { useState, useRef, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface TooltipProps {
  content: string;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ content, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Calculate optimal position to keep tooltip in viewport
  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8; // Space between trigger and tooltip

    // Check if tooltip fits below
    if (triggerRect.bottom + tooltipRect.height + padding < viewportHeight) {
      setPosition('bottom');
    }
    // Check if tooltip fits above
    else if (triggerRect.top - tooltipRect.height - padding > 0) {
      setPosition('top');
    }
    // Check if tooltip fits to the right
    else if (triggerRect.right + tooltipRect.width + padding < viewportWidth) {
      setPosition('right');
    }
    // Check if tooltip fits to the left
    else if (triggerRect.left - tooltipRect.width - padding > 0) {
      setPosition('left');
    }
    // Default to bottom if nothing fits perfectly
    else {
      setPosition('bottom');
    }
  };

  // Handle click outside to close tooltip
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible]);

  // Recalculate position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure tooltip is rendered
      setTimeout(calculatePosition, 10);
    }
  }, [isVisible]);

  const toggleTooltip = () => {
    setIsVisible(!isVisible);
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  // Calculate tooltip position dynamically
  const getTooltipStyle = (): React.CSSProperties => {
    if (!triggerRef.current) return {};

    const rect = triggerRef.current.getBoundingClientRect();

    switch (position) {
      case 'bottom':
        return {
          top: `${rect.bottom + 8}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)',
        };
      case 'top':
        return {
          top: `${rect.top - 8}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translate(-50%, -100%)',
        };
      case 'right':
        return {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.right + 8}px`,
          transform: 'translateY(-50%)',
        };
      case 'left':
        return {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.left - 8}px`,
          transform: 'translate(-100%, -50%)',
        };
      default:
        return {};
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        ref={triggerRef}
        onClick={toggleTooltip}
        aria-label="More information"
        className="text-slate-400 hover:text-cyan-400 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/50 rounded"
        type="button"
      >
        <InformationCircleIcon className="w-4 h-4" />
      </button>

      {isVisible && triggerRef.current && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] w-64 max-w-xs"
          role="tooltip"
          style={getTooltipStyle()}
        >
          <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-3">
            <p className="text-xs text-slate-300 leading-relaxed">
              {content}
            </p>
            {/* Arrow indicator */}
            <div
              className={`absolute w-2 h-2 bg-slate-900 border-slate-700 transform rotate-45 ${
                position === 'bottom' ? 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 border-l border-t' :
                position === 'top' ? 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 border-r border-b' :
                position === 'right' ? 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-l border-b' :
                'right-0 top-1/2 translate-x-1/2 -translate-y-1/2 border-r border-t'
              }`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
